<template>
    <div class="modal fade viewmodal" tabindex="-1" role="dialog" aria-hidden="true" style="padding-right: 0px !important" ref="modal">
        <div class="modal-dialog modal-full" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <nav class="navbar navbar-light w-100">
                      <slot name="quote-option-selector-button"></slot>
                      <div class="buttons">
                        <a class="btn" data-dismiss="modal" aria-label="Stäng" @click="userSelected = undefined">
                          <i class="fal fa-times"></i>
                        </a>
                      </div>
                      <slot name="quote-option-selector-collapsible"></slot>
                    </nav>
                </div>

                <div class="modal-body" @scroll="handleModalScroll">
                  <div v-if="obscured" class="expired-overlay d-flex flex-column align-items-center justify-content-center">
                    <i class="fal fa-lock fa-3x mb-3"></i>
                    <p class="h5 mb-1">{{ $t('pdfView.offer_expired') }}</p>
                    <small class="text-muted">{{ $t('pdfView.offer_expired_contact') }}</small>
                  </div>
                  <div class="mb-5" v-for="(document, idx) in documents" :class="{ 'document-obscured': obscured }">
                    <div :id="`pdf-container-${idx}`" class="d-flex justify-content-between p-2 bg-light">
                      <div class="font-weight-bold">
                        {{document.pdf_name}}
                      </div>
                      <div v-if="!obscured" class="d-flex justify-content-between">
                        <a v-if="printUrl" :href="printUrl" class="btn print">
                          <i class="fal fa-print"></i>
                        </a>

                        <a :href="document.pdf_url" class="btn download" download>
                          <i class="fal fa-download"></i>
                        </a>
                      </div>
                    </div>
                    <pdf-all v-if="!deferReader || readerVisible" :pdflink="document.pdf_url" :is-pdf="document.is_pdf"></pdf-all>
                  </div>
                </div>

                <div class="modal-footer" v-if="!isSigned">
                    <a v-if="isSignable" href="#" style="color:black" @click="openModal(signModalTarget);">
                        <div class="signbuttonmodal">
                          {{signCopy}}
                          <i class="far fa-arrow-right ml-2"></i>
                        </div>
                    </a>
                    <a v-else href="#" style="color:black" @click="openModal(contactModalTarget);">
                        <div class="signbuttonmodal">
                          {{contactCopy}}
                          <i class="fal fa-comments wiggle ml-2"></i>
                        </div>
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
nav.navbar {
    display: flex !important;
}
.header-company-image {
  max-width: 50%;
  max-height: 80px;
  width: auto;
  height: auto;
}
.modal-header {
    position: relative;

    button[aria-expanded="true"] {
        i.when-collapsed { display: none; }
    }

    button[aria-expanded="false"] {
        i.when-expanded { display: none; }
    }

    .dropdown-container {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;

        display: flex;
        align-items: center;
        justify-content: center;
    }
}

.modal-body {
    overflow-y: auto;
    height: calc(100vh - 196px);

    &:deep(*) {
      .page-container {
        margin: 5px -16px;
        padding: 0px;

        canvas {
          border-left: 0;
          border-right: 0;
          border-bottom: 0;
        }

        &:first-child canvas {
          border-top: 0;
        }
      }
    }
}
.modal-footer {
    justify-content: center;
}

.buttons {
  margin-left: auto;

  a {
    font-size: 1.35rem;
  }
  a.print {
    font-size: 1.40rem;
    margin-right: 1.5rem;
  }
  a.download {
    font-size: 1.40rem;
    margin-right: 1.5rem;
  }
}

.expired-overlay {
  position: sticky;
  top: 0;
  z-index: 10;
  background: rgba(255, 255, 255, 0.92);
  padding: 2rem;
  text-align: center;
  color: #555;
}

.document-obscured {
  filter: blur(6px);
  pointer-events: none;
  user-select: none;
}
</style>

<script>

import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';

export default {
    inject: { sbModal: { default: null } },
    props: {
        companyImageUrl: String,
        quoteKey: String,
        quoteOptionId: Number,
        quoteOptionName: String,
        printUrl: { type: String, default: null },
        mainPdfName: String,
        mainPdfId: Number,
        mainPdfUrl: String,
        mainPdfIsPdf: Boolean,
        isSigned: Boolean,
        isPostSale: Boolean,
        deferReader: Boolean,
        extraDocuments: {
            type: Array,
            default: () => []
        },
        initiallySelected: {
            type: Number,
            default: 0
        },
        signCopy: {
            type: String,
            default: 'Gå vidare'
        },
        signModalTarget: {
            type: String,
            default: '#bankid'
        },
        companyColor: {
            type: String,
            default: ''
        },
        obscured: {
            type: Boolean,
            default: false
        },
        signable: {
            type: Boolean,
            default: true
        },
        contactCopy: {
            type: String,
            default: ''
        },
        contactModalTarget: {
            type: String,
            default: '#callback'
        }
    },
    data() {
        return {
            uuid: uuidv4(),
            userSelected: undefined,
            logged: new Set(),
            logging: new Set(),
            visitId: 0,
            presenceChannel: null,
            returnFocus: null,
            readerVisible: false,
            unmounting: false,
        }
    },
    computed: {
        documents: function(){
          return [{pdf_name: this.mainPdfName, pdf_url: this.mainPdfUrl, is_pdf: this.mainPdfIsPdf, id: this.mainPdfId}].concat(this.extraDocuments);
        },
        selected: function() {
            return this.userSelected !== undefined ? this.userSelected : this.initiallySelected;
        },
        isSignable: function() {
            return this.signable && !this.obscured;
        }
    },
    mounted() {
        this.bindModalEvents();
        window.addEventListener('pagehide', this.leaveAlternativePresence);
    },
    beforeUnmount() {
        this.unmounting = true;
        this.disposeModal();
        window.jQuery(this.$refs["modal"]).off(`.${this.uuid}`);
        window.removeEventListener('pagehide', this.leaveAlternativePresence);
        this.leaveAlternativePresence();
    },
  watch: {
       async selected(){
         await this.$nextTick();
         await this.logDocumentOpen();
      },
       quoteOptionId(newQuoteOptionId, previousQuoteOptionId){
         if (window.jQuery(this.$refs["modal"]).hasClass('show')) {
             this.leaveAlternativePresence();
             this.userSelected = 0;
             this.startVisit();
             this.logDocumentOpen();
             this.dispatchPackageViewEvent('quote-package-view-end', previousQuoteOptionId);
             this.dispatchPackageViewEvent('quote-package-view-start', newQuoteOptionId);
             this.joinAlternativePresence();
         }
       }
  },
  methods: {
        bindModalEvents() {
            const thisModal = window.jQuery(this.$refs.modal);
            thisModal.on(`show.bs.modal.${this.uuid}`, (event) => {
                this.returnFocus = event.relatedTarget ?? document.activeElement;
            });
            thisModal.on(`shown.bs.modal.${this.uuid}`, () => {
                this.readerVisible = true;
                this.startVisit();
                this.logDocumentOpen();
                this.dispatchPackageViewEvent('quote-package-view-start', this.quoteOptionId);
                this.joinAlternativePresence();
            });
            thisModal.on(`hidden.bs.modal.${this.uuid}`, () => {
                this.dispatchPackageViewEvent('quote-package-view-end', this.quoteOptionId);
                this.leaveAlternativePresence();
                this.userSelected = undefined;
                this.readerVisible = false;
                thisModal.modal('dispose');
                if (!this.unmounting) {
                    this.bindModalEvents();
                }
                this.sbModal?.restoreAfterChildModal(this.returnFocus);
            });
        },
        disposeModal() {
            const thisModal = window.jQuery(this.$refs.modal);
            const instance = thisModal.data('bs.modal');
            if (!instance) {
                return;
            }

            // Bootstrap 4 ignores hide during a fade. Finish only this instance's
            // transitions, then run its synchronous hide lifecycle before Vue removes it.
            window.jQuery(instance._backdrop).trigger('transitionend');
            thisModal.find('.modal-dialog').trigger('transitionend');
            thisModal.trigger('transitionend');
            window.jQuery(instance._backdrop).trigger('transitionend');
            if (thisModal.data('bs.modal')) {
                thisModal.removeClass('fade').modal('hide');
            }
        },
        startVisit() {
            this.visitId += 1;
            this.logged = new Set();
            this.logging = new Set();
        },
        alternativePresenceChannel() {
            return `Quote.View.${this.quoteKey}.Option.${this.quoteOptionId}`;
        },
        joinAlternativePresence() {
            if (this.isPostSale || !this.quoteOptionId) {
                return;
            }

            const channelName = this.alternativePresenceChannel();
            if (this.presenceChannel === channelName) {
                return;
            }

            this.leaveAlternativePresence();
            this.presenceChannel = channelName;
            window.Echo.join(channelName).error((error) => {
                if (this.presenceChannel === channelName) {
                    this.leaveAlternativePresence();
                }

                window.SalesByteCaptureException(error, {
                    tags: { feature: 'option-alternative-presence' },
                    extra: { channelName },
                });
            });
        },
        leaveAlternativePresence() {
            if (!this.presenceChannel) {
                return;
            }

            window.Echo.leave(this.presenceChannel);
            this.presenceChannel = null;
        },
        async handleModalScroll(){
          const pattern = /^pdf-container-\d+$/;
          Array.from(document.querySelectorAll('div[id^="pdf-container-"]'))
            .filter(div => pattern.test(div.id))
            .forEach((div) => {
            const {top, bottom} = div.getBoundingClientRect();
            const vHeight = (window.innerHeight || document.documentElement.clientHeight);

            if ((top > 0 || bottom > 0) && top < (vHeight/2)){
              this.userSelected = Number(div.id.split("-")[2]);
            }
          })
        },
        async logDocumentOpen() {
            const selected = this.selected;
            const openingKey = `${this.visitId}:${this.quoteOptionId}:${selected}`;
            if (this.logged.has(openingKey) || this.logging.has(openingKey)){
                return;
            }

            const document = this.documents[selected];
            if (!document) {
                return;
            }

            this.logging.add(openingKey);
            let logged = false;

            try {
                if (this.isPostSale) {
                    logged = await this.$actionLog.logQuoteDocumentOpen(this.quoteKey, document.pdf_name, document.id);
                } else if (selected === 0) {
                    await axios.post(this.route('quote.options.storeOpened', [this.quoteKey, this.quoteOptionId]));
                    logged = true;
                } else {
                    logged = await this.$actionLog.logQuoteOptionExtraDocumentOpen(this.quoteKey, this.quoteOptionName + "/" + document.pdf_name);
                }
            } catch (error) {
                window.SalesByteCaptureException(error, {
                    tags: { feature: 'document-opening' },
                    extra: { quoteOptionId: this.quoteOptionId, selected },
                });
            } finally {
                this.logging.delete(openingKey);
            }

            if (logged) {
                this.logged.add(openingKey);
            }
        },
        dispatchPackageViewEvent(name, packageId) {
          if (this.isPostSale || !packageId) {
            return;
          }
          document.dispatchEvent(new CustomEvent(name, { detail: { packageId } }));
        },
        openModal(modalId) {
          this.$emit('move-forward-with-quote-option',this.quoteOptionId);
          window.jQuery(this.$refs["modal"]).one('hidden.bs.modal', () => window.jQuery(modalId).modal('show'));
          window.jQuery(this.$refs["modal"]).modal('hide');
        }
    }
}
</script>
